import { NotificationAvatar } from '@/components/notifications/NotificationAvatar';
import {
  getNotificationActionKey,
  getNotificationI18nParams,
  getNotificationActorName,
  isAttentionNotification,
  isBoostNeedsAttention,
  getNotificationBodyKey,
} from '@/components/notifications/notification-presentation';
import { formatRelativeTime } from '@/lib/format-relative-time';
import type { NotificationDto } from '@fluenceur/types';
import { NotificationCategory, NotificationType } from '@fluenceur/types';
import { ThemedButton } from '@fluenceur/ui';
import { Box, chakra, Circle, Flex, HStack, IconButton, Text } from '@chakra-ui/react';
import { NavArrowRight, Xmark } from 'iconoir-react';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type NotificationItemProps = {
  isDismissPending: boolean;
  isLast: boolean;
  isMarkReadPending: boolean;
  notification: NotificationDto;
  onDismiss: (notificationId: string) => void;
  onOpen: (notification: NotificationDto) => void;
};

const ROW_INTRINSIC_SIZE = 'auto 84px';

export const NotificationItem = memo(function NotificationItem({
  isDismissPending,
  isLast,
  isMarkReadPending,
  notification,
  onDismiss,
  onOpen,
}: NotificationItemProps) {
  const { t, i18n } = useTranslation();
  const unread = !notification.readAt;
  const params = getNotificationI18nParams(notification, i18n.language);
  const title = t(notification.titleKey, params);
  const body = resolveBody(notification, params, t);
  const occurredAt = formatRelativeTime(notification.occurredAt, i18n.language);
  const actionKey = getNotificationActionKey(notification);

  const handleOpen = useCallback(() => {
    onOpen(notification);
  }, [notification, onOpen]);

  const handleDismiss = useCallback(() => {
    onDismiss(notification.id);
  }, [notification.id, onDismiss]);

  return (
    <Flex
      position="relative"
      gap="12px"
      px="16px"
      py="12px"
      bg={unread ? 'babyPowder' : 'panelBg'}
      transition="background 0.15s"
      _hover={{ bg: 'softCream' }}
      // Inset hairline under the text column rather than a full-bleed rule.
      _after={
        isLast
          ? undefined
          : {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: '64px',
              right: '16px',
              h: '1px',
              bg: 'border.subtle',
            }
      }
      css={{
        contentVisibility: 'auto',
        containIntrinsicSize: ROW_INTRINSIC_SIZE,
        // The dismiss control shows on hover or keyboard focus within the row,
        // and always on touch screens, which have no hover to reveal it.
        '& [data-dismiss]': { opacity: 0 },
        '&:hover [data-dismiss], &:focus-within [data-dismiss]': { opacity: 1 },
        '@media (hover: none)': { '& [data-dismiss]': { opacity: 1 } },
      }}
    >
      {/*
        Stretched click target covering the row. It sits above the text but
        below the two real controls, so all three stay independently focusable
        and the row avoids the `nested-interactive` ARIA violation.
      */}
      <chakra.button
        type="button"
        aria-label={title}
        disabled={isMarkReadPending}
        onClick={handleOpen}
        position="absolute"
        inset={0}
        zIndex={1}
        cursor="pointer"
        _focusVisible={{ outline: '2px solid', outlineColor: 'ink', outlineOffset: '-2px' }}
      />

      <NotificationAvatar
        actorName={getNotificationActorName(notification)}
        avatarUrl={notification.actor?.avatarUrl ?? null}
        isAgent={notification.category === NotificationCategory.AGENT}
        needsAttention={isAttentionNotification(notification)}
      />

      <Box flex="1" minW={0}>
        <HStack gap="8px" align="center" minH="20px">
          <Text
            flex="1"
            minW={0}
            fontFamily="heading"
            fontSize="14px"
            fontWeight="semibold"
            color="ink"
            lineHeight="1.4"
            lineClamp={2}
          >
            {title}
          </Text>

          {unread && (
            <Circle
              role="img"
              aria-label={t('notifications.unread')}
              flexShrink={0}
              size="8px"
              bg="rose.500"
            />
          )}

          <Text flexShrink={0} fontSize="12px" color="inkMuted" whiteSpace="nowrap">
            {occurredAt}
          </Text>

          <IconButton
            data-dismiss=""
            aria-label={t('notifications.dismiss')}
            variant="ghost"
            rounded="full"
            boxSize="28px"
            minW="28px"
            p={0}
            my="-4px"
            mr="-6px"
            flexShrink={0}
            position="relative"
            zIndex={2}
            color="inkMuted"
            transition="opacity 0.15s, background 0.15s"
            disabled={isDismissPending}
            onClick={handleDismiss}
            _hover={{ bg: 'primary.100', color: 'ink' }}
            _focusVisible={{ bg: 'primary.100', color: 'ink' }}
            css={{ '& svg': { width: '14px', height: '14px' } }}
          >
            <Xmark />
          </IconButton>
        </HStack>

        <Text mt="2px" fontSize="13px" lineHeight="1.45" color="inkMuted" lineClamp={2}>
          {body}
        </Text>

        {actionKey && (
          <ThemedButton
            variant="secondary"
            size="chip"
            mt="10px"
            gap="6px"
            position="relative"
            zIndex={2}
            disabled={isMarkReadPending}
            onClick={handleOpen}
            // The button recipe's `_icon` slot forces svg dimensions, so the
            // width/height attributes alone would render at the recipe size.
            css={{ '& svg': { width: '14px', height: '14px' } }}
          >
            {t(actionKey)}
            <NavArrowRight aria-hidden="true" />
          </ThemedButton>
        )}
      </Box>
    </Flex>
  );
});

/**
 * Boost delivery failures interpolate a machine reason code into the body copy;
 * every other type renders its body key directly.
 */
function resolveBody(
  notification: NotificationDto,
  params: NotificationDto['i18nParams'],
  t: ReturnType<typeof useTranslation>['t']
): string {
  if (isBoostNeedsAttention(notification)) {
    return t(notification.bodyKey, {
      ...params,
      reason: t(`notifications.boost.delivery.reasons.${notification.data.reasonCode}`, {
        defaultValue: t('notifications.boost.delivery.reasonFallback'),
      }),
    });
  }

  // A creator the brand cannot see the name of yet reads as an unnamed message.
  if (
    notification.type === NotificationType.CONVERSATION_CREATOR_MESSAGE_RECEIVED &&
    !notification.data.creatorDisplayName
  ) {
    return t('notifications.conversation.creatorMessage.bodyUnnamed');
  }

  return t(getNotificationBodyKey(notification), params);
}
